import { LanguageVariantsSection } from "@/components/language-variants-section";
import { BuilderShell } from "@/components/studio/builder-shell";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import { ReadOnlyBanner } from "@/components/studio/read-only-banner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useContentLifecycle } from "@/hooks/use-content-lifecycle";
import { useToast } from "@/hooks/use-toast";
import { categoryService } from "@/lib/api/categories.service";
import { ContentChallengeService } from "@/lib/api/challenge.service";
import { coachService } from "@/lib/api/coachService";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import type {
  Category,
  Coach,
  StudioContentStatus,
  TagGroup,
  TagOption,
} from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  SUPPORTED_LANGUAGES,
} from "@/lib/studio-constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { ChallengeVariantFields } from "./challenge-variant-fields";

const challengeConfig = CONTENT_TYPE_CONFIG.challenge;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

interface ChallengeLanguage {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription?: string | null;
  audioContentId?: string | null;
  audioFileUrl?: string | null;
  audioFileName?: string | null;
  durationSeconds?: number | null;
  thumbnailUrl?: string | null;
}

interface ChallengeVersion {
  id: string;
  version: number;
  status: StudioContentStatus;
  languages: ChallengeLanguage[];
}

interface ChallengeResponse {
  id: string;
  categoryId: string | null;
  createdBy?: string | null;
  authorCoachId?: string | null;
  subCategoryIds?: string[];
  tagOptionIds: string[];
  versions: ChallengeVersion[];
  publishedVersionId: string | null;
}

interface VariantDraft {
  existingLanguage: boolean;
  languageId?: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  audioContentId: string | null;
  audioFileUrl: string | null;
  audioFileName: string | null;
  durationSeconds: number | null;
  thumbnailUrl: string | null;
}

export default function ChallengeEditPage({ id }: { id: string }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const selfCoachId = user?.id ?? null;

  const { data: challenge, isLoading } = useQuery<ChallengeResponse>({
    queryKey: ["/content-challenges", id],
  });

  const editableVersion = challenge?.versions?.find(
    (v) => v.status === "draft" || v.status === "changes_requested",
  );
  const latestVersion = challenge?.versions?.[0];
  const displayVersion = editableVersion ?? latestVersion;

  const [categoryId, setCategoryId] = useState("");
  const [authorCoachId, setAuthorCoachId] = useState<string | null>(null);
  const [selectedTagOptionIds, setSelectedTagOptionIds] = useState<string[]>(
    [],
  );
  const [variants, setVariants] = useState<VariantDraft[]>([]);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: coaches = [] } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((r) => r.coaches),
    enabled: !isCoachOnly,
  });

  useEffect(() => {
    if (isCoachOnly && selfCoachId && !authorCoachId && loadedKey) {
      setAuthorCoachId(selfCoachId);
    }
  }, [isCoachOnly, selfCoachId, authorCoachId, loadedKey]);

  useEffect(() => {
    if (!challenge) return;
    const ver = editableVersion ?? latestVersion;
    if (!ver) return;
    const nextKey = `${id}:${ver.id}:${ver.version}`;
    if (loadedKey === nextKey) return;
    setCategoryId(challenge.categoryId || "");
    setAuthorCoachId(challenge.authorCoachId || null);
    setSelectedTagOptionIds(challenge.tagOptionIds || []);
    const languages = ver.languages || [];
    const mapped: VariantDraft[] = languages.map((l) => ({
      existingLanguage: true,
      languageId: l.id,
      languageCode: l.languageCode,
      isDefault: l.isDefault,
      localizedTitle: l.localizedTitle,
      audioContentId: l.audioContentId || null,
      audioFileUrl: l.audioFileUrl || null,
      audioFileName: l.audioFileName || null,
      durationSeconds: l.durationSeconds ?? null,
      thumbnailUrl: l.thumbnailUrl || null,
    }));
    setVariants(
      mapped.length > 0
        ? mapped
        : [
            {
              existingLanguage: false,
              languageCode: "en",
              isDefault: true,
              localizedTitle: "",
              audioContentId: null,
              audioFileUrl: null,
              audioFileName: null,
              durationSeconds: null,
              thumbnailUrl: null,
            },
          ],
    );
    setActiveVariantIndex(0);
    setLoadedKey(nextKey);
  }, [challenge, editableVersion, latestVersion, id, loadedKey]);

  const status = displayVersion?.status as StudioContentStatus | undefined;
  const {
    submitMutation,
    withdrawMutation,
    archiveMutation,
    createVersionMutation,
    isReadOnly,
    isOwner,
    canCreateVersion,
  } = useContentLifecycle({
    contentType: "challenge",
    apiPath: "/content-challenges",
    id,
    status,
    createdBy: challenge?.createdBy,
    authorCoachId: challenge?.authorCoachId,
    listRoute: "/studio/challenges",
    editRoute: (newId) => `/studio/challenges/${newId}/edit`,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!editableVersion) {
        throw new Error(
          "No editable version. Create a new version first.",
        );
      }
      const versionId = editableVersion.id;

      await api.put(`/content-challenges/${id}`, {
        categoryId: categoryId || undefined,
        authorCoachId: authorCoachId || undefined,
        tagOptionIds: selectedTagOptionIds,
      });

      const newLanguages: VariantDraft[] = [];
      for (const v of variants) {
        if (!v.localizedTitle.trim()) continue;
        if (v.existingLanguage) {
          // audioContentId is the authoritative link. Send it on every
          // update so server can resolve snapshot. Don't ship audioFileUrl
          // — server-side snapshot fields are owned by audioContentId.
          await api.put(
            `/content-challenges/${id}/version/${versionId}/language/${v.languageCode}`,
            {
              localizedTitle: v.localizedTitle,
              audioContentId: v.audioContentId ?? null,
              isDefault: v.isDefault,
            },
          );
        } else {
          newLanguages.push(v);
        }
      }

      if (newLanguages.length > 0) {
        await api.post(
          `/content-challenges/${id}/version/${versionId}/languages`,
          {
            languages: newLanguages.map((v) => ({
              languageCode: v.languageCode,
              isDefault: v.isDefault,
              localizedTitle: v.localizedTitle,
              audioContentId: v.audioContentId || undefined,
            })),
          },
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/content-challenges"] });
      queryClient.invalidateQueries({
        queryKey: ["/content-challenges", id],
      });
      toast({ title: "Challenge saved" });
    },
    onError: (err: Error) => {
      toast({
        title: err?.message || "Failed to save",
        variant: "destructive",
      });
    },
  });

  const removeLanguageMutation = useMutation({
    mutationFn: async (languageId: string) => {
      await ContentChallengeService.deleteVersionLanguage(languageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/content-challenges", id],
      });
      toast({ title: "Language removed" });
    },
    onError: (err: Error) => {
      toast({
        title: err?.message || "Failed to remove language",
        variant: "destructive",
      });
    },
  });

  const updateVariant = (field: keyof VariantDraft, value: any) => {
    setVariants((prev) => {
      const updated = [...prev];
      updated[activeVariantIndex] = {
        ...updated[activeVariantIndex],
        [field]: value,
      };
      return updated;
    });
  };

  const addLanguageVariant = (langCode: string) => {
    setVariants((prev) => {
      if (prev.some((v) => v.languageCode === langCode)) return prev;
      const next = [
        ...prev,
        {
          existingLanguage: false,
          languageCode: langCode,
          isDefault: false,
          localizedTitle: "",
          audioContentId: null,
          audioFileUrl: null,
          audioFileName: null,
          durationSeconds: null,
          thumbnailUrl: null,
        },
      ];
      setActiveVariantIndex(next.length - 1);
      return next;
    });
  };

  const confirmRemoveLanguageVariant = async () => {
    if (pendingRemoveIndex === null) return;
    const v = variants[pendingRemoveIndex];
    if (v.isDefault) {
      toast({
        title: "Cannot remove default language",
        variant: "destructive",
      });
      setPendingRemoveIndex(null);
      return;
    }
    if (variants.length <= 1) {
      toast({
        title: "At least one language variant must remain",
        variant: "destructive",
      });
      setPendingRemoveIndex(null);
      return;
    }
    if (v.existingLanguage && v.languageId) {
      // Persisted language: server DELETE triggers query invalidate +
      // loadedKey-driven rehydrate. Drop loadedKey so local state replaces
      // on next render.
      try {
        await removeLanguageMutation.mutateAsync(v.languageId);
        setLoadedKey(null);
      } catch {
        // mutation surfaces toast; keep local state as-is
      }
    } else {
      // Unsaved local variant: drop from state directly
      setVariants((prev) => prev.filter((_, i) => i !== pendingRemoveIndex));
      setActiveVariantIndex((prev) =>
        Math.max(0, Math.min(prev, variants.length - 2)),
      );
    }
    setPendingRemoveIndex(null);
  };

  const setAsDefault = (index: number) => {
    if (index < 0 || index >= variants.length) return;
    const updated = variants.map((v, i) => ({ ...v, isDefault: i === index }));
    setVariants(updated);
    setActiveVariantIndex(index);
  };

  const toggleTagOption = (tagId: string) => {
    setSelectedTagOptionIds((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId],
    );
  };

  const getGroupOptions = (groupId: string) =>
    tagOptions.filter((to) => to.groupId === groupId);

  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !variants.some((v) => v.languageCode === lang.code),
  );

  const activeVariant = variants[activeVariantIndex];
  const defaultTitle =
    variants.find((v) => v.isDefault)?.localizedTitle ||
    variants[0]?.localizedTitle ||
    undefined;

  return (
    <BuilderShell
      contentTypeConfig={challengeConfig}
      mode="edit"
      title={defaultTitle}
      version={displayVersion?.version}
      status={status}
      isLoading={isLoading}
      notFound={!isLoading && !challenge}
      isReadOnly={isReadOnly || !editableVersion}
      isSaving={saveMutation.isPending}
      isSubmitting={submitMutation.isPending}
      isWithdrawing={withdrawMutation.isPending}
      isCreatingVersion={createVersionMutation.isPending}
      canCreateVersion={canCreateVersion}
      onSave={() => saveMutation.mutate()}
      onSubmit={() => submitMutation.mutate(id)}
      onWithdraw={() => withdrawMutation.mutate(id)}
      onCreateVersion={() => createVersionMutation.mutate()}
    >
      {isReadOnly && <ReadOnlyBanner ownedByOther={!isOwner} />}
      <IntentTaggingSection
        stepNumber={1}
        categories={categories}
        categoryId={categoryId}
        onCategoryChange={setCategoryId}
        tagGroups={tagGroups}
        getGroupOptions={getGroupOptions}
        selectedTagOptionIds={selectedTagOptionIds}
        onToggleTagOption={toggleTagOption}
        isReadOnly={isReadOnly || !editableVersion}
      />

      <Card className="p-4 space-y-2">
        <Label>Author Coach</Label>
        {isCoachOnly ? (
          <Input
            value={
              (challenge as any)?.authorCoachName ??
              (authorCoachId === user?.id ? (user?.name ?? "") : "")
            }
            disabled
            data-testid="select-author-coach"
          />
        ) : (
          <Select
            value={authorCoachId || "none"}
            onValueChange={(val) =>
              setAuthorCoachId(val === "none" ? null : val)
            }
            disabled={isReadOnly || !editableVersion}
          >
            <SelectTrigger data-testid="select-author-coach">
              <SelectValue placeholder="None" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {coaches.map((coach) => (
                <SelectItem key={coach.id} value={coach.id}>
                  {coach.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Card>

      <LanguageVariantsSection
        stepNumber={2}
        variants={variants}
        activeVariantIndex={activeVariantIndex}
        onActiveVariantChange={setActiveVariantIndex}
        availableLanguages={availableLanguages}
        onAddVariant={addLanguageVariant}
        onSetDefault={setAsDefault}
        pendingRemoveIndex={pendingRemoveIndex}
        onRequestRemove={setPendingRemoveIndex}
        onConfirmRemove={confirmRemoveLanguageVariant}
        onCancelRemove={() => setPendingRemoveIndex(null)}
        isReadOnly={isReadOnly || !editableVersion}
      >
        {activeVariant && (
          <ChallengeVariantFields
            variant={activeVariant}
            onFieldChange={updateVariant}
            isReadOnly={isReadOnly || !editableVersion}
            currentCategoryId={categoryId || null}
            currentTagOptionIds={selectedTagOptionIds}
            allTagOptions={tagOptions}
          />
        )}
      </LanguageVariantsSection>
    </BuilderShell>
  );
}
